import React, { useEffect, useState, useMemo, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { useNavigate, useLocation } from 'react-router-dom';
import '../css/ParticipantList.css';
import '../css/SearchTable.css';
import axios from 'axios';
import { resolvePersonImageUrl } from '../../utils/resolvePersonImageUrl';
import { formatAccreditationCategoryLabel } from '../../utils/invitationTypeFormioUtils';

const truncateSelectLabel = (label, maxLen = 32) => {
  const text = String(label || '').trim();
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen - 1)}…`;
};

/** Header + body same padding/widths so columns line up */
const searchTableStyles = {
  table: {
    style: {
      backgroundColor: '#FFFBF4',
    },
  },
  headRow: {
    style: {
      backgroundColor: '#D9A128',
      minHeight: '52px',
      borderBottom: 'none',
    },
  },
  headCells: {
    style: {
      color: '#ffffff',
      fontWeight: '600',
      backgroundColor: '#D9A128',
      justifyContent: 'flex-start',
      textAlign: 'left',
      paddingLeft: '12px',
      paddingRight: '12px',
      borderRight: 'none',
    },
  },
  rows: {
    style: {
      backgroundColor: '#FFFBF4',
      minHeight: '56px',
      borderBottom: '1px solid #eee',
    },
  },
  cells: {
    style: {
      backgroundColor: '#FFFBF4',
      color: '#333',
      justifyContent: 'flex-start',
      textAlign: 'left',
      paddingLeft: '12px',
      paddingRight: '12px',
      borderRight: 'none',
    },
  },
  pagination: {
    style: {
      backgroundColor: '#FFFBF4',
      borderTop: '1px solid #efe6d6',
    },
  },
};

const getRegistrationBadgeClass = (status) => {
  switch (status) {
    case 'Added':
      return 'badge bg-secondary participant-badge-registration';
    case 'Pending':
      return 'badge bg-warning text-white participant-badge-registration';
    case 'Registered':
      return 'badge bg-success participant-badge-registration';
    default:
      return 'badge bg-secondary';
  }
};

const personIdOf = (participant) =>
  String(participant?.person_id?._id || participant?.person_id || '');

const SearchTable = () => {
  const API_URL = process.env.REACT_APP_API_URL;

  const [persons, setPersons] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [invitationTypes, setInvitationTypes] = useState([]);
  const [selectedInvitationType, setSelectedInvitationType] = useState('');
  const [emsRoles, setEmsRoles] = useState([]);
  const [selectedEmsRole, setSelectedEmsRole] = useState('');
  const [accreditationCategories, setAccreditationCategories] = useState([]);
  const [selectedAccreditationCategory, setSelectedAccreditationCategory] = useState('');
  const [assignedParticipants, setAssignedParticipants] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [competitionId, setCompetitionId] = useState('');
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();

  const fetchAssignedParticipants = useCallback(async (compId) => {
    if (!compId) {
      setAssignedParticipants([]);
      return;
    }
    const participantRes = await axios.get(`${API_URL}/ems/participants?competition_id=${compId}`);
    setAssignedParticipants(Array.isArray(participantRes.data) ? participantRes.data : []);
  }, [API_URL]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          personRes,
          activecompetitionRes,
          groupRes,
          invitationTypeRes,
          emsRoleRes,
          accreditationCategoryRes,
        ] = await Promise.all([
          axios.get(`${API_URL}/persons`),
          axios.get(`${API_URL}/ems/competitions?active=true`),
          axios.get(`${API_URL}/ems/groups`),
          axios.get(`${API_URL}/ems/invitation-types`),
          axios.get(`${API_URL}/ems/roles`),
          axios.get(`${API_URL}/ems/accreditation-categories`),
        ]);

        const activecompetition = Array.isArray(activecompetitionRes.data)
          ? activecompetitionRes.data[0]
          : null;
        const compId = activecompetition?._id || '';
        setCompetitionId(compId);

        if (compId) {
          await fetchAssignedParticipants(compId);
        }

        setPersons(personRes.data || []);
        setGroups(Array.isArray(groupRes.data) ? groupRes.data : []);
        setInvitationTypes(Array.isArray(invitationTypeRes.data) ? invitationTypeRes.data : []);
        setEmsRoles(Array.isArray(emsRoleRes.data) ? emsRoleRes.data : []);
        setAccreditationCategories(
          Array.isArray(accreditationCategoryRes.data) ? accreditationCategoryRes.data : []
        );
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setGroups([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_URL, fetchAssignedParticipants]);

  useEffect(() => {
    if (location.state?.searchValue) {
      setSearchTerm(location.state.searchValue);
    }
  }, [location.state]);

  const participantByPersonId = useMemo(() => {
    const map = {};
    (assignedParticipants || []).forEach((p) => {
      const pid = personIdOf(p);
      if (pid) map[pid] = p;
    });
    return map;
  }, [assignedParticipants]);

  const filteredPersons = useMemo(() => {
    let filtered = Array.isArray(persons) ? [...persons] : [];
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((p) => {
        const fullName = `${p?.name || ''} ${p?.mname || ''} ${p?.lname || ''}`.toLowerCase();
        return fullName.includes(term) || (p?.email || '').toLowerCase().includes(term);
      });
    }
    return filtered;
  }, [persons, searchTerm]);

  const handleRowClick = (row) => {
    if (!row?._id) return;
    navigate(`/edit-person/${row._id}`, { state: { person: row } });
  };

  const columns = [
    {
      name: 'Status',
      cell: (row) => {
        const participant = participantByPersonId[String(row?._id)];
        if (!participant) {
          return <span className="text-muted small">Not assigned</span>;
        }
        const status = participant.registration_status || 'Added';
        return (
          <span className={getRegistrationBadgeClass(status)} title="Registration status">
            {status}
          </span>
        );
      },
      width: '140px',
      grow: 0,
      ignoreRowClick: true,
    },
    {
      name: 'Image',
      cell: (row) => (
        <img
          src={resolvePersonImageUrl(row?.image, API_URL) || '/images/avatar.png'}
          alt="profile"
          style={{ width: 40, height: 40, borderRadius: '50%' }}
        />
      ),
      width: '90px',
      grow: 0,
      ignoreRowClick: true,
    },
    {
      name: 'Name',
      selector: (row) => `${row?.name || ''} ${row?.mname || ''} ${row?.lname || ''}`,
      sortable: true,
      grow: 2,
      minWidth: '180px',
      cell: (row) => (
        <span
          className="text-secondary fw-semibold"
          style={{ cursor: 'pointer' }}
          onClick={() => handleRowClick(row)}
        >
          {row?.name} {row?.mname || ''} {row?.lname}
        </span>
      ),
    },
    {
      name: 'Email',
      selector: (row) => row?.email || '-',
      sortable: true,
      grow: 2,
      minWidth: '180px',
    },
    {
      name: 'Country Code',
      selector: (row) => row?.country_id?.code || '-',
      width: '130px',
      grow: 0,
    },
  ];

  const handleAssign = async () => {
    if (!selectedGroup) {
      alert('Please select a group before assigning participants.');
      return;
    }
    const person_ids = selectedRows.map((p) => p?._id).filter(Boolean);
    if (person_ids.length === 0) {
      alert('No participants selected.');
      return;
    }
    await axios.post(`${API_URL}/ems/participants/bulk-add`, {
      person_ids,
      competition_id: competitionId,
      group_id: selectedGroup,
      invitation_type_id: selectedInvitationType || null,
      role_id: selectedEmsRole || null,
      accreditation_category_id: selectedAccreditationCategory || null,
    });
    await fetchAssignedParticipants(competitionId);
    alert(`${person_ids.length} participants added successfully!`);
  };

  return (
    <div className="search-table-container">
      {/* Same top offset as ParticipantsList (.pl-single-row--toolbar = sidebar gold panel) */}
      <div className="pl-single-row pl-single-row--toolbar search-assign-toolbar mb-2">
        <div className="pl-single-item search-assign-search">
          <input
            type="text"
            className="form-control form-control-sm search-input"
            placeholder="Search name or email"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ border: '1px solid #d1cfcf', width: '100%' }}
            aria-label="Search name or email"
          />
        </div>

        <div className="pl-single-item">
          <select
            className="form-select form-select-sm search-assign-select search-assign-select--group"
            value={selectedGroup}
            onChange={(e) => setSelectedGroup(e.target.value)}
            required
            title={groups.find((g) => String(g?._id) === String(selectedGroup))?.group_name || ''}
            aria-label="Select Group"
          >
            <option value="">Select Group</option>
            {groups.map((g) => {
              const name = g?.group_name || 'Unnamed';
              return (
                <option key={g?._id || g} value={g?._id || ''} title={name}>
                  {truncateSelectLabel(name)}
                </option>
              );
            })}
          </select>
        </div>

        <div className="pl-single-item">
          <select
            className="form-select form-select-sm search-assign-select search-assign-select--role"
            value={selectedEmsRole}
            onChange={(e) => setSelectedEmsRole(e.target.value)}
            title={emsRoles.find((r) => String(r?._id) === String(selectedEmsRole))?.role_name || ''}
            aria-label="Select Role"
          >
            <option value="">Select Role</option>
            {emsRoles.map((r) => {
              const name = r?.role_name || 'Unnamed';
              return (
                <option key={r?._id} value={r?._id || ''} title={name}>
                  {truncateSelectLabel(name)}
                </option>
              );
            })}
          </select>
        </div>

        <div className="pl-single-item">
          <select
            className="form-select form-select-sm search-assign-select search-assign-select--invitation"
            value={selectedInvitationType}
            onChange={(e) => setSelectedInvitationType(e.target.value)}
            title={
              invitationTypes.find((t) => String(t?._id) === String(selectedInvitationType))?.name ||
              ''
            }
            aria-label="Select Invitation Type"
          >
            <option value="">Select Invitation Type</option>
            {invitationTypes.map((t) => {
              const name = t?.name || 'Unnamed';
              return (
                <option key={t?._id} value={t?._id || ''} title={name}>
                  {truncateSelectLabel(name)}
                </option>
              );
            })}
          </select>
        </div>

        <div className="pl-single-item">
          <select
            className="form-select form-select-sm search-assign-select search-assign-select--accreditation"
            value={selectedAccreditationCategory}
            onChange={(e) => setSelectedAccreditationCategory(e.target.value)}
            title={formatAccreditationCategoryLabel(
              accreditationCategories.find(
                (c) => String(c?._id) === String(selectedAccreditationCategory)
              )
            )}
            aria-label="Select Accreditation Category"
          >
            <option value="">Select Accreditation Category</option>
            {accreditationCategories.map((c) => {
              const name = formatAccreditationCategoryLabel(c);
              return (
                <option key={c?._id} value={c?._id || ''} title={name}>
                  {truncateSelectLabel(name)}
                </option>
              );
            })}
          </select>
        </div>

        <button
          type="button"
          className="btn search-assign-submit-btn"
          onClick={handleAssign}
        >
          Assign Participants to Competition
        </button>

        {!loading && filteredPersons.length === 0 && searchTerm.trim() !== '' && (
          <button
            type="button"
            className="btn search-assign-add-btn"
            onClick={() =>
              navigate('/ems/persons/add-person', { state: { searchValue: searchTerm } })
            }
          >
            + Add Participant
          </button>
        )}
      </div>

      <div className="search-table-wrapper">
        <DataTable
          columns={columns}
          data={filteredPersons}
          progressPending={loading}
          pagination
          paginationPerPage={50}
          paginationComponentOptions={{ noRowsPerPage: true, rangeSeparatorText: 'of' }}
          paginationTotalRows={filteredPersons.length}
          highlightOnHover={false}
          pointerOnHover={false}
          striped={false}
          selectableRows
          selectableRowDisabled={(row) => Boolean(participantByPersonId[String(row?._id)])}
          responsive
          onSelectedRowsChange={(e) => setSelectedRows(e.selectedRows)}
          onRowClicked={handleRowClick}
          customStyles={searchTableStyles}
          dense={false}
        />
      </div>

      {filteredPersons.length === 0 && (
        <div className="text-center mt-3">
          <p>No individuals found.</p>
        </div>
      )}
    </div>
  );
};

export default SearchTable;
